import { useState, useContext, useEffect } from 'react';
import type { ReactNode } from 'react';
import { useParams } from 'react-router-dom';

import { useUserQuiz, useResetQuizSession } from '@openclassrooms/data-connect-web/users';
import {
  useCreateQuizSession,
  useExerciseSession,
} from '@openclassrooms/data-connect-web/assessment';
import type { QuizInformation, ExerciseSession } from '@openclassrooms/sdk-web';
import { STATUSES } from '@openclassrooms/ui-web/learning-activities';

import CourseExerciseContext from './courseExerciseContext';
import { useErrorHandlers } from './errorHandlerContext';
import courseContext from './courseContext';

interface Props {
  children: ReactNode;
}

type RouteParams = {
  courseIdSlug: string;
  exerciseId: string;
};

interface CreateQuizSessionData {
  exerciseSessionId: number;
}

export default function CourseExerciseContextProvider({ children }: Props) {
  const { currentUser } = useContext(courseContext);
  const { exerciseId: exerciseIdParam } = useParams<RouteParams>();
  const { onError } = useErrorHandlers();

  const { id: userId } = currentUser || {};

  const [exerciseSession, setExerciseSession] = useState<ExerciseSession | null>(null);
  const [quizInformation, setQuizInformation] = useState<QuizInformation | null>(null);
  const [hasResetQuiz, setHasResetQuiz] = useState<boolean | null>(null);

  const [createQuizSession] = useCreateQuizSession({
    userId,
    quizId: exerciseIdParam,
  });

  const [, { mutateAsync: resetQuizSession }] = useResetQuizSession();

  const onSuccess = async (quizSessionData: QuizInformation) => {
    const quizInformationData = { ...quizSessionData };

    if (!quizSessionData?.quizSessionId) {
      createQuizSession(
        { userId, quizId: exerciseIdParam },
        {
          onSuccess: (data) => {
            const { exerciseSessionId } = data as CreateQuizSessionData;
            quizInformationData.quizSessionId = exerciseSessionId;
          },
          onError,
        }
      );
    }

    if (
      quizInformationData.learningActivityStatus === STATUSES.STATUS_IN_PROGRESS &&
      hasResetQuiz
    ) {
      quizInformationData.learningActivityStatus = STATUSES.STATUS_CAN_RETRY;
    }

    setQuizInformation(quizInformationData);
  };

  const { refetch: refetchQuizInformation } = useUserQuiz(
    { userId, quizId: exerciseIdParam },
    { enabled: Boolean(userId), onSuccess, onError, refetchOnWindowFocus: false }
  );

  useEffect(() => {
    if (!exerciseSession) {
      return;
    }

    if (Number(exerciseIdParam) !== exerciseSession.id) {
      setExerciseSession(null);
    }
  }, [exerciseIdParam, exerciseSession]);

  useEffect(
    () => {
      (async () => {
        if (
          quizInformation &&
          quizInformation.learningActivityStatus === STATUSES.STATUS_CAN_RETRY &&
          hasResetQuiz === null
        ) {
          await resetQuizSession({ userId, quizId: exerciseIdParam }, { onError });
          refetchQuizInformation();
          setHasResetQuiz(true);
        } else if (quizInformation) {
          setHasResetQuiz(false);
        }
      })();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [quizInformation]
  );

  const { quizSessionId } = quizInformation || {};

  const { isInitialLoading } = useExerciseSession(
    {
      exerciseSessionId: quizSessionId,
    },
    {
      enabled: Boolean(quizSessionId) && hasResetQuiz === false,
      onSuccess: (data: ExerciseSession) => {
        setExerciseSession(data);
      },
      onError,
      refetchOnWindowFocus: false,
    }
  );

  return (
    <CourseExerciseContext.Provider
      value={{
        exerciseId: Number(exerciseIdParam),
        exerciseSession,
        isLoading: isInitialLoading,
        setExerciseSession,
        quizInformation,
        refetchQuizInformation,
      }}
    >
      {children}
    </CourseExerciseContext.Provider>
  );
}
